import { supabase } from "@/integrations/supabase/client";

/**
 * Wait for Supabase auth to report a live session, polling + listening to
 * `onAuthStateChange` so we don't race `ProtectedRoute` after an OAuth
 * callback. Resolves with `true` if a session is present within `timeoutMs`,
 * `false` otherwise.
 */
export async function waitForSession(timeoutMs = 4000): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  if (data.session) return true;

  return new Promise<boolean>((resolve) => {
    let done = false;
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) finish(true);
    });
    const timer = setTimeout(() => finish(false), timeoutMs);
    function finish(ok: boolean) {
      if (done) return;
      done = true;
      listener.subscription.unsubscribe();
      clearTimeout(timer);
      resolve(ok);
    }
  });
}

/**
 * Make sure the signed-in user has a profile + agency row. Safe to call on
 * every sign-in — the underlying SQL function is idempotent. Failures are
 * swallowed (logged) so they never block the user from proceeding.
 */
export async function ensureProfile(): Promise<void> {
  try {
    const { error } = await (supabase as any).rpc("ensure_profile");
    if (error && import.meta.env.DEV) {
      console.warn("[ensureProfile] rpc error:", error.message);
    }
  } catch (e) {
    if (import.meta.env.DEV) console.warn("[ensureProfile] threw:", e);
  }
}